import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionCostDetail } from './hooks/useConstructionCost';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  pending: 'bg-amber-100 text-amber-600',
  reviewed: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-500 text-white',
  rejected: 'bg-red-500 text-white',
  paid: 'bg-indigo-500 text-white',
  locked: 'bg-slate-900 text-white',
};

export default function CostTrackingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { cost: item, isLoading, updateStatus } = useConstructionCostDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết chi phí...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin chi phí</div>;

  const handleApprove = async () => {
    try {
      await updateStatus({ status: 'approved' });
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'source', label: 'Nguồn phát sinh' },
    { value: 'boq', label: 'BOQ liên quan' },
    { value: 'documents', label: 'Chứng từ/Hóa đơn' },
    { value: 'approvals', label: 'Phê duyệt' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.description}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono italic">#{item.id.substring(0, 8).toUpperCase()}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            Ngày ghi: <span className="font-bold text-os-primary">{new Date(item.cost_date).toLocaleDateString('vi-VN')}</span>
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Chi phí', path: '/construction/cost-tracking' },
        { label: 'Chi tiết' }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'pending' && (
            <AuthButton module="payments" action="approve" variant="primary" icon="ri-check-line" onClick={handleApprove}>
              Phê duyệt chi phí
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-printer-line">In phiếu chi</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-6 bg-slate-50 border-none shadow-sm flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 font-bold  tracking-wider mb-1">Giá trị thực tế</span>
                    <span className="text-2xl font-bold text-slate-900">{item.actual_amount.toLocaleString()} <span className="text-sm font-normal text-slate-400">VNĐ</span></span>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <i className="ri-bank-card-line text-xl" />
                  </div>
                </Card>
                <Card className="p-6 bg-slate-50 border-none shadow-sm flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 font-bold  tracking-wider mb-1">Dự toán (Planned)</span>
                    <span className="text-2xl font-bold text-slate-400 line-through">{item.planned_amount?.toLocaleString() || '---'}</span>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-slate-200 flex items-center justify-center text-slate-500">
                    <i className="ri-scales-line text-xl" />
                  </div>
                </Card>
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Phân bổ chi tiết
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-slate-700">{item.zone?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Loại chi phí</span>
                    <Badge variant="neutral" className="text-[10px]  font-bold">{item.cost_type}</Badge>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Đơn vị tính</span>
                    <span className="text-sm font-bold text-slate-700">{item.quantity} {item.unit}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Đơn giá</span>
                    <span className="text-sm font-bold text-slate-700">{item.unit_cost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Hạng mục BOQ</span>
                    <span className="text-sm font-bold text-os-primary underline italic">View BOQ Item</span>
                  </div>
                </div>

                {item.note && (
                  <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <p className="text-[10px] text-amber-600 font-bold  mb-1">Ghi chú nội bộ</p>
                    <p className="text-xs text-amber-700 italic">{item.note}</p>
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-history-line text-slate-400" />
                  Nguồn dữ liệu gốc (Source Traceability)
                </h3>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-os-primary/10 flex items-center justify-center text-os-primary">
                    <i className="ri-link-m" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-bold text-slate-900 ">Loại nguồn: {item.source_type || 'Manual'}</p>
                    <p className="text-xs text-slate-500 italic">Mã liên kết: {item.source_id || '---'}</p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate(`/construction/work-orders/detail/${item.work_order_id}`)}>
                    Xem nguồn gốc
                  </Button>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-user-follow-line" />
                  Người ghi nhận & Đối soát
                </h4>
                <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-2xl border border-slate-700">
                  <Avatar name={item.recorder?.full_name} src={item.recorder?.avatar_url} size="sm" />
                  <div className="flex flex-col">
                    <p className="text-[10px] text-slate-400 font-bold ">Người tạo</p>
                    <p className="text-sm font-bold text-slate-200">{item.recorder?.full_name}</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-slate-800 rounded-2xl border border-slate-700">
                  <p className="text-[10px] text-slate-400 font-bold  mb-1">Kiểm tra bởi QS</p>
                  <p className="text-xs font-bold text-os-primary italic">Đã kiểm tra khối lượng</p>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-slate-400" />
                  Dòng thời gian phê duyệt
                </h4>
                <div className="space-y-6 relative before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-slate-100">
                  <div className="relative pl-6 flex flex-col">
                    <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-emerald-500 border-4 border-white shadow-sm" />
                    <span className="text-[10px] font-bold text-slate-900">Khởi tạo chi phí</span>
                    <span className="text-[9px] text-slate-400">12/04/2026 - {item.recorder?.full_name}</span>
                  </div>
                  <div className="relative pl-6 flex flex-col">
                    <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-slate-200 border-4 border-white shadow-sm" />
                    <span className="text-[10px] font-bold text-slate-400 italic text-center line-clamp-1">QS Review & Verification</span>
                    <span className="text-[9px] text-slate-400">Đang chờ QS kiểm tra</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'source' || activeTab === 'boq' || activeTab === 'documents' || activeTab === 'approvals' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ các chứng từ kế toán, nhật ký phê duyệt đa cấp và hồ sơ đối soát QS thực tế."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
